import {
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildPanelShape } from '@toolproof-core/visualization/_pure/shapes';
import type {
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toPrimitiveBandPosition, toPrimitiveBandRotation, toPrimitiveBandSpin } from '@/adapters/primitive/_pure/derivation/constellations';
import {
    toPrimitiveHubCollectionKey,
    toPrimitiveHubConstellationKey,
    toPrimitiveHubItemKey,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import { toPrimitiveHubOpacity, toPrimitiveHubRingRadius } from '@/adapters/primitive/_pure/derivation/wheels';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

type HubDerivations = {
    itemCollectionDerivations: EntityItemCollectionDerivation[];
    constellationDerivations: EntityConstellationDerivation[];
};

/** Placeholders and helpers already render as the hub ring; travellers leave theirs at the source wheel. */
function hasHub(wheel: PrimitiveWheelState): boolean {
    if (wheel.valueKind === 'comparison-helper') return false;
    return wheel.variant === undefined;
}

/**
 * Transient Natural wheels own an invisible hub so its item lives exactly as long as the wheel; it shows only as
 * the hint ring of an entering helper, fading as the bands grow out. Concentric members share one visible ring.
 */
function toHubOpacityByWheel(wheels: readonly PrimitiveWheelState[]): Map<PrimitiveWheelState, number> {
    const hintPositions = new Set<string>();
    const opacityByWheel = new Map<PrimitiveWheelState, number>();
    for (const wheel of wheels) {
        if (hasHub(wheel)) {
            opacityByWheel.set(wheel, toPrimitiveHubOpacity(wheel));
            continue;
        }
        if ((wheel.valueKind ?? 'natural') !== 'natural' || wheel.powers.length === 0) continue;
        const position = JSON.stringify(toPrimitiveBandPosition(wheel, toHubPower(wheel)));
        const hinted = wheel.entranceGrowth !== undefined && !hintPositions.has(position);
        if (hinted) hintPositions.add(position);
        opacityByWheel.set(wheel, hinted ? 1 - wheel.entranceGrowth! : 0);
    }
    return opacityByWheel;
}

function toHubPower(wheel: PrimitiveWheelState): number {
    return wheel.variant === undefined ? 0 : Math.min(...wheel.powers);
}

function toHubRef(wheel: PrimitiveWheelState) {
    return { wheelRole: wheel.role, variant: wheel.variant, ...(wheel.variant === undefined ? {} : { power: toHubPower(wheel) }) };
}

export function buildPrimitiveHubDerivations(
    config: Config,
    wheels: readonly PrimitiveWheelState[],
): HubDerivations {
    const hubRadius = toPrimitiveHubRingRadius(config);
    const { outlineColor, outlineOpacity, outlineWidth } = config.base.display.placeholder;
    const opacityByWheel = toHubOpacityByWheel(wheels);
    const hubWheels = [...opacityByWheel.keys()];

    return {
        itemCollectionDerivations: hubWheels.map((wheel) => toEntityItemCollectionDerivation({
            itemCollectionKey: toPrimitiveHubCollectionKey(toHubRef(wheel)),
            itemDerivations: [defineEntityItemDerivation({
                item: {
                    itemKind: 'entity',
                    itemKey: toPrimitiveHubItemKey(toHubRef(wheel)),
                    name: `${wheel.role} hub`,
                    description: 'Hub ring where the 10^0 band meets the wheel axis.',
                },
                parametricState: {
                    // Matches the placeholder disc so a settling wheel leaves the hub unchanged.
                    shapePresentation: {
                        appearanceMode: 'outlined' as const,
                        border: {
                            color: outlineColor,
                            opacity: outlineOpacity,
                            width: outlineWidth,
                        },
                    },
                    visualStyle: {
                        color: outlineColor,
                        opacity: opacityByWheel.get(wheel)!,
                    },
                },
            })],
        })),
        constellationDerivations: hubWheels.map((wheel) => defineEntityConstellationDerivation({
            constellation: {
                constellationKind: 'entity',
                constellationKey: toPrimitiveHubConstellationKey(toHubRef(wheel)),
                itemCollectionKey: toPrimitiveHubCollectionKey(toHubRef(wheel)),
                anchor: {
                    itemKey: toPrimitiveHubItemKey(toHubRef(wheel)),
                    anchorKey: 'center',
                },
                meta: { wheelRole: wheel.role },
            },
            parametricState: {
                layout: {
                    layoutKind: 'sector-ring',
                    anchor: { anchorKind: 'position', position: toPrimitiveBandPosition(wheel, toHubPower(wheel)) },
                    innerRadius: hubRadius,
                    outerRadius: hubRadius,
                    angularGapFraction: 0,
                    arcSegments: config.base.display.wheel.arcSegments,
                    rotation: wheel.wheelRotation ?? toPrimitiveBandRotation(toPrimitiveBandSpin(wheel, toHubPower(wheel))),
                },
            },
        })),
    };
}
